// =============================================================================
// SIREPPAR - Página Mi Partido (Vista de Usuario Externo)
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { partidoService, expedienteService, api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useAlertasPartido } from '../../hooks/useAlertasPartido';
import { tipoTramiteLabel } from '../../utils/tiposExpediente';

const estadoConfig: Record<string, { color: string; label: string }> = {
  EN_FORMACION: { color: 'bg-yellow-100 text-yellow-800', label: 'En Formación' },
  PENDIENTE_REVISION: { color: 'bg-blue-100 text-blue-800', label: 'En Revisión' },
  RECONOCIDO: { color: 'bg-green-100 text-green-800', label: 'Reconocido' },
  VIGENTE: { color: 'bg-green-100 text-green-800', label: 'Vigente' },
  SUSPENDIDO: { color: 'bg-red-100 text-red-800', label: 'Suspendido' },
  CADUCO: { color: 'bg-orange-100 text-orange-800', label: 'Caduco' },
  EXTINGUIDO: { color: 'bg-gray-100 text-gray-800', label: 'Extinguido' },
};

export default function MiPartidoPage() {
  const { user } = useAuth();

  // Query partido del usuario (include=all trae _count y autoridades vigentes)
  const { data: partido, isLoading: loadingPartido } = useQuery({
    queryKey: ['mi-partido', user?.partidoId],
    queryFn: async () => {
      if (!user?.partidoId) return null;
      const response = await partidoService.getById(user.partidoId, 'all');
      return response.data.data;
    },
    enabled: !!user?.partidoId,
  });

  // Resumen de afiliados (válidos / total) del partido
  const { data: resumenAfiliados } = useQuery({
    queryKey: ['mi-partido-afiliados-resumen', user?.partidoId],
    queryFn: async () => {
      if (!user?.partidoId) return null;
      const response = await api.get(`/afiliados/partido/${user.partidoId}/resumen`);
      return response.data;
    },
    enabled: !!user?.partidoId,
  });

  // Observaciones / intimaciones pendientes (desde las alertas del partido)
  const { data: alertasData } = useAlertasPartido();
  const obsPendientes = alertasData?.alertas.filter(
    (a) => a.tipo === 'OBSERVACION_PENDIENTE',
  ).length ?? 0;

  // Query expedientes del partido
  const { data: expedientes, isLoading: loadingExpedientes } = useQuery({
    queryKey: ['expedientes-partido', user?.partidoId],
    queryFn: async () => {
      if (!user?.partidoId) return [];
      const response = await expedienteService.list({ partidoId: user.partidoId, pageSize: 10 });
      return response.data.data;
    },
    enabled: !!user?.partidoId,
  });

  // Redes sociales y fecha de la última elección interna: el partido las declara en
  // el wizard de autoridades definitivas y NO tienen columna en `partidos`, así que
  // viven en el datosFormulario del expediente de RECONOCIMIENTO. Se toma el más
  // reciente de ese tipo (si rehizo el trámite, el último es el que vale).
  const datosReconocimiento = (() => {
    const recon = (expedientes ?? [])
      .filter((e: any) => e.tipo === 'RECONOCIMIENTO' && e.datosFormulario)
      .sort((a: any, b: any) => new Date(b.fechaCreacion).getTime() - new Date(a.fechaCreacion).getTime())[0];
    const df = recon?.datosFormulario as Record<string, unknown> | undefined;
    if (!df) return null;
    return {
      redesSociales: typeof df.redesSociales === 'string' ? df.redesSociales : null,
      fechaUltimaEleccionInterna:
        typeof df.fechaUltimaEleccionInterna === 'string' ? df.fechaUltimaEleccionInterna : null,
    };
  })();

  if (!user?.partidoId) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto h-16 w-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
          <span className="text-3xl">⚠️</span>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Sin Partido Asignado</h2>
        <p className="text-gray-500">No tiene un partido político asignado a su cuenta.</p>
        <p className="text-gray-500 mt-2">Contacte al administrador del sistema.</p>
      </div>
    );
  }

  if (loadingPartido) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!partido) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No se encontró información del partido.</p>
      </div>
    );
  }

  const estado = estadoConfig[partido.estado] || estadoConfig.EN_FORMACION;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 bg-primary-100 rounded-lg flex items-center justify-center">
              <span className="text-3xl font-bold text-primary-600">{partido.numero}</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{partido.denominacion}</h1>
              <p className="text-gray-500">{partido.sigla}</p>
              <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${estado.color}`}>
                {estado.label}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Acciones rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/documentos/subir"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow border-l-4 border-green-500"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📎</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Subir Documento</h3>
              <p className="text-sm text-gray-500">Adjuntar documentación</p>
            </div>
          </div>
        </Link>

        <Link
          to="/autoridades"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow border-l-4 border-orange-500"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">👥</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Autoridades</h3>
              <p className="text-sm text-gray-500">Gestionar autoridades</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Información del partido */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Datos generales */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="font-semibold text-gray-900">Información General</h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-500">Tipo</span>
                <p className="font-medium">{partido.tipo || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Fecha Constitución</span>
                <p className="font-medium">
                  {partido.fechaConstitucion 
                    ? new Date(partido.fechaConstitucion).toLocaleDateString('es-AR')
                    : '-'}
                </p>
              </div>
              <div className="col-span-2">
                <span className="text-sm text-gray-500">Domicilio Legal</span>
                <p className="font-medium">{partido.domicilioLegal || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Localidad</span>
                <p className="font-medium">{partido.localidad || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Código Postal</span>
                <p className="font-medium">{partido.codigoPostal || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Email</span>
                <p className="font-medium">{partido.emailOficial || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Teléfono</span>
                <p className="font-medium">{partido.telefonoOficial || '-'}</p>
              </div>
              {/* Datos que el partido cargó en el wizard de autoridades definitivas
                  y que antes no se mostraban acá. El sitio web tiene columna propia;
                  las redes y la fecha de la última interna viven en el JSON del
                  expediente de reconocimiento (ver datosReconocimiento). */}
              <div>
                <span className="text-sm text-gray-500">Sitio web</span>
                <p className="font-medium break-all">
                  {partido.sitioWeb
                    ? <a href={partido.sitioWeb.startsWith('http') ? partido.sitioWeb : `https://${partido.sitioWeb}`}
                         target="_blank" rel="noreferrer"
                         className="text-primary-600 hover:underline">{partido.sitioWeb}</a>
                    : '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Redes sociales</span>
                <p className="font-medium break-all">{datosReconocimiento?.redesSociales || '-'}</p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Última elección interna</span>
                <p className="font-medium">
                  {datosReconocimiento?.fechaUltimaEleccionInterna
                    ? new Date(datosReconocimiento.fechaUltimaEleccionInterna).toLocaleDateString('es-AR')
                    : '-'}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500">Resolución de reconocimiento</span>
                <p className="font-medium">{partido.resolucionReconocimiento || '-'}</p>
              </div>
              {partido.fechaReconocimiento && (
                <div>
                  <span className="text-sm text-gray-500">Fecha de reconocimiento</span>
                  <p className="font-medium">{new Date(partido.fechaReconocimiento).toLocaleDateString('es-AR')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="font-semibold text-gray-900">Estadísticas</h2>
          </div>
          <div className="px-6 divide-y divide-gray-100">
            <div className="flex items-center justify-between py-4">
              <span className="text-sm text-gray-600">Afiliados válidos / total</span>
              <span className="text-lg font-semibold text-gray-900 tabular-nums">
                {resumenAfiliados?.afiliadosValidos ?? 0} / {resumenAfiliados?.total ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-4">
              <span className="text-sm text-gray-600">Expedientes</span>
              <span className="text-lg font-semibold text-gray-900 tabular-nums">
                {partido._count?.expedientes ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-4">
              <span className="text-sm text-gray-600">Autoridades vigentes</span>
              <span className="text-lg font-semibold text-gray-900 tabular-nums">
                {partido.autoridades?.length ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between py-4">
              <div>
                <span className="block text-sm text-gray-600">Observaciones / intimaciones pendientes</span>
                <Link to="/dashboard#alertas" className="text-sm text-primary-600 hover:text-primary-800">
                  Ver notificaciones →
                </Link>
              </div>
              <span className="text-lg font-semibold text-gray-900 tabular-nums">
                {obsPendientes}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Expedientes recientes */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Expedientes Recientes</h2>
          <Link to="/expedientes" className="text-primary-600 hover:text-primary-800 text-sm">
            Ver todos →
          </Link>
        </div>
        <div className="divide-y">
          {loadingExpedientes ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : expedientes && expedientes.length > 0 ? (
            expedientes.map((exp: any) => (
              <Link
                key={exp.id}
                to={`/expedientes/${exp.id}`}
                className="block px-6 py-4 hover:bg-gray-50"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{exp.numero} — {tipoTramiteLabel(exp.tipo)}</p>
                    <p className="text-sm text-gray-500">{exp.asunto}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    exp.estado === 'APROBADO' ? 'bg-green-100 text-green-800' :
                    exp.estado === 'RECHAZADO' ? 'bg-red-100 text-red-800' :
                    exp.estado === 'OBSERVADO' ? 'bg-orange-100 text-orange-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {exp.estado}
                  </span>
                </div>
              </Link>
            ))
          ) : (
            <div className="p-6 text-center text-gray-500">
              No hay expedientes registrados
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
